import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useObserver } from 'mobx-react-lite';

import { useModals, ModalNames } from 'components/modals';
import useUser from 'hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { usePhoneVerificationNudgeOperational } from 'src/hooks/use-phone-verification-nudge-operational';
import { usePhoneVerificationNudgeAnalytics } from 'src/hooks/use-phone-verification-nudge-analytics';
import { usePhoneVerification } from 'src/components/modals/phone-verification-nudge/use-phone-verification';
import {
  deriveNudgeLeg,
  parseSendStatus,
  parseStatusValue,
} from 'src/components/modals/phone-verification-nudge/phone-verification.types';
import type { OtpHostContext, OtpHostOutcome } from './otp-host';

/**
 * The banner is a projection of `phoneVerificationStatus`, not a one-shot "should we show a CTA"
 * flag -- `leg_one` (unverified, phone on file) gets the actionable "Verify Now" row;
 * `verified_other` gets an informational row naming the account that owns the number, using the
 * `ownerEmailHint` that's already present on that same status response. Every other status
 * (`add_phone`, `verified_self`, `nudge_disabled`, unknown, or no response at all) renders nothing.
 */
export type CtaState =
  | {
      kind: 'verified_other';
      dispensaryId?: string | null;
      enterpriseId?: string | null;
      ownerEmailHint?: string | null;
    }
  | { kind: 'leg_one'; dispensaryId?: string | null; enterpriseId?: string | null };

export type BannerErrorKind = 'invalid_number' | 'rate_limited' | 'unavailable';

export type BannerError = {
  kind: BannerErrorKind;
};

export type UsePhoneVerificationCtaResult = {
  isVisible: boolean;
  isSending: boolean;
  cta: CtaState | null;
  bannerError: BannerError | null;
  onVerifyClick: () => Promise<void>;
};

type SendPayload =
  | {
      status?: string | null;
      phoneHint?: string | null;
      errorCode?: string | null;
    }
  | null
  | undefined;

/**
 * Maps a `sendPhoneVerificationCode` result that ISN'T `sent` onto a banner error variant. Under
 * the FINTECH-4689 contract, rate limits and invalid/undeliverable numbers arrive as a 2xx
 * carrying `errorCode` rather than a thrown error; `quarantined`/`not_verifiable`/`nudge_disabled`
 * arrive as `status`. Anything unrecognized fails safe to the generic "unavailable" copy rather
 * than a silent no-op banner.
 */
function mapSendFailure(payload: SendPayload): BannerError {
  if (payload?.errorCode === 'rate_limited') {
    return { kind: 'rate_limited' };
  }
  if (payload?.errorCode === 'invalid_number' || payload?.errorCode === 'undeliverable') {
    return { kind: 'invalid_number' };
  }
  return { kind: 'unavailable' };
}

/**
 * Gating + status fetch + click/outcome logic for the "Verify your phone number" banner on My
 * Account. The banner is a projection of the latest `phoneVerificationStatus` response --
 * `leg_one` renders the actionable row, `verified_other` renders the informational
 * belongs-to-another-account row (using the hint already present on that response), and every
 * other status renders nothing. Deliberately independent of `usePhoneVerificationNudge` (the
 * post-login orchestrator): that hook bails on `UI.isCheckoutPage`, `Modals.activeModals.size > 0`,
 * and its own in-flight ref, none of which should block a deliberate user click on this CTA.
 */
export function usePhoneVerificationCta(): UsePhoneVerificationCtaResult {
  const { openModal } = useModals();
  const user = useUser();
  const isLoggedIn = useObserver(() => user.exists);
  // Observed so the status-fetch effect below re-runs whenever the phone changes -- e.g. after a
  // My Account save unverifies a newly-changed number (`updateProfileV2` + `user.refetch()` in
  // `my-account.jsx`). `user.profile` can be `{}` before the user has loaded, hence the optional
  // chain (same pattern as `payments/dutchie-pay/signup/views/aggregator-widget/index.tsx`).
  const phone = useObserver(() => user.profile?.phone);
  // `useDispensary` is an untyped JS hook whose return shape narrows based on whether a `cName`
  // route param is present -- on `/account/*` (no `cName`) it omits `dispensary` entirely and
  // returns an `error` on the result instead (see `use-dispensary.js`). That `error` is the
  // normal dutchie.com case here, not a failure -- it is deliberately not read.
  // Read `dispensary.id` (the resolved ObjectId), NOT the hook's own `dispensaryId` field -- the
  // latter is just the raw route param (e.g. the `cName` slug on `/embedded-menu/[cName]/...`),
  // which never matches Armageddon's per-dispensary LD targeting and always evaluates as
  // `nudge_disabled`. See FINTECH-4747.
  const { dispensary } = useDispensary() as {
    dispensary?: { id?: string | null; retailer?: { enterpriseId?: string | null } | null };
  };
  const dispensaryId = dispensary?.id ?? null;
  // FINTECH-4632: always dispensary-derived, never present without a dispensaryId alongside it --
  // this is caller-supplied LD targeting context only, not a new authorization surface.
  const enterpriseId = dispensary?.retailer?.enterpriseId ?? null;
  const { fetchStatus, sendCode } = usePhoneVerification();
  const operational = usePhoneVerificationNudgeOperational();
  // This surface is deliberately independent of the post-login orchestrator (see the doc comment
  // above), so it gets its own `surface: 'myAccountBanner'` analytics -- `shown`/`verify_clicked`/
  // `code_sent` own this hook; `otp_submitted`/`otp_resent` belong to the OTP host below;
  // `completed` and `failed` (post-send) are owned HERE, not by the host, even though the host
  // resolves the outcome -- see its own doc comment for why.
  const analytics = usePhoneVerificationNudgeAnalytics({ surface: 'myAccountBanner' });

  const [cta, setCta] = useState<CtaState | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [bannerError, setBannerError] = useState<BannerError | null>(null);
  // Guards the `shown` emission below to fire once per transition INTO a visible state, not on
  // every effect re-run -- the effect re-runs on dispensaryId/enterpriseId/phone/refreshToken
  // changes, most of which leave `cta.kind` unchanged.
  const previousCtaKindRef = useRef<CtaState['kind'] | null>(null);
  // Bumped to force the status-fetch effect below to re-run on demand -- the same mechanism the
  // effect already uses when the observed phone changes, reused here so a verify-time `conflict`
  // doesn't need its own bespoke fetch: it just invalidates status and lets the projection recompute.
  const [refreshToken, setRefreshToken] = useState(0);
  const requestIdRef = useRef(0);
  // Set immediately before bumping `refreshToken` from a `conflict` outcome, and read (then
  // cleared) inside the effect run it triggers. Its only job is to pick the right failure
  // fallback: a `conflict` just told the user their number belongs to someone else, so if the
  // recovery fetch itself throws, degrade to the hint-less copy rather than the normal silent
  // "hide the banner" posture used for every other fetch failure.
  const conflictRecoveryRef = useRef(false);

  useEffect(() => {
    // Bump the request id on every effect run (including the early-return branch) so any
    // still-in-flight response from a previous run is recognized as stale and ignored.
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const isConflictRecovery = conflictRecoveryRef.current;
    let cancelled = false;

    if (!isLoggedIn || !operational) {
      // Logged-out or flag-off: clear to hidden and issue no request at all. The flag check
      // gates the effect BEFORE `fetchStatus` is ever called -- its only job is to skip the
      // network call, not to make a rollout decision.
      setCta(null);
      previousCtaKindRef.current = null;
      conflictRecoveryRef.current = false;
      return () => {
        cancelled = true;
      };
    }

    const run = async (): Promise<void> => {
      try {
        const { phoneVerificationStatus } = await fetchStatus(dispensaryId, enterpriseId);

        if (cancelled || requestId !== requestIdRef.current) {
          // A newer effect run has already superseded this one -- do not clobber its result.
          return;
        }

        // A fresh, network-verified status supersedes any stale UI-only error banner from a
        // previous click (e.g. the user fixed their number and it's unverified again).
        setBannerError(null);

        const leg = deriveNudgeLeg(phoneVerificationStatus?.status, phoneVerificationStatus?.hasPhone);
        const status = parseStatusValue(phoneVerificationStatus?.status);

        if (leg === 'leg_one') {
          // Store the exact dispensaryId/enterpriseId used for THIS fetch, so `sendCode`/
          // `verifyCode` always agree with what `fetchStatus` was called with -- never re-read
          // `useDispensary()` at click time (Armageddon's `PhoneNumbers::VerificationGate`
          // branches on both).
          setCta({ kind: 'leg_one', dispensaryId, enterpriseId });
          if (previousCtaKindRef.current !== 'leg_one') {
            analytics.track('shown', { leg: 'leg_one' });
          }
          previousCtaKindRef.current = 'leg_one';
        } else if (status === 'verified_other') {
          setCta({
            kind: 'verified_other',
            dispensaryId,
            enterpriseId,
            ownerEmailHint: phoneVerificationStatus?.ownerEmailHint ?? null,
          });
          if (previousCtaKindRef.current !== 'verified_other') {
            analytics.track('shown', { leg: 'leg_two' });
          }
          previousCtaKindRef.current = 'verified_other';
        } else {
          setCta(null);
          previousCtaKindRef.current = null;
        }
      } catch {
        if (!cancelled && requestId === requestIdRef.current) {
          if (isConflictRecovery) {
            // The verify-time conflict already told us the number belongs to someone else --
            // if the recovery fetch itself fails, still show the (hint-less) informational row
            // rather than leaving the user with a blank page after a deliberate action. Routed
            // through the same shown-emission-and-ref-update path the try block's own
            // 'verified_other' branch uses, so a hidden -> visible transition here (e.g. a prior
            // fetch failure had already reset the ref to null below) still emits `shown` instead
            // of being silently skipped.
            setCta({ kind: 'verified_other', dispensaryId, enterpriseId, ownerEmailHint: null });
            if (previousCtaKindRef.current !== 'verified_other') {
              analytics.track('shown', { leg: 'leg_two' });
            }
            previousCtaKindRef.current = 'verified_other';
          } else {
            // Silent by design -- no toast, no ernie, no user-visible error. Same posture as the
            // post-login orchestrator. Reset the ref alongside `cta` (mirroring the try block's
            // own "no leg" branch above) -- otherwise a transient failure here leaves a stale
            // 'leg_one'/'verified_other' behind, and a later refresh that restores the SAME leg
            // silently skips its `shown` emission despite the banner having gone hidden -> visible.
            setCta(null);
            previousCtaKindRef.current = null;
          }
        }
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          conflictRecoveryRef.current = false;
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, operational, dispensaryId, enterpriseId, phone, refreshToken]);

  const onVerifyClick = useCallback(async (): Promise<void> => {
    if (cta?.kind !== 'leg_one' || isSending) {
      // Guards against a double-send on rapid clicks -- `isSending` flips true synchronously
      // below, before the first `await`. Also guards against a stray click while the
      // informational (buttonless) row is showing -- it renders no button, but this stays
      // defensive in case that ever changes.
      return;
    }

    analytics.trackVerifyClicked();
    setIsSending(true);
    try {
      const result = await sendCode(cta.dispensaryId, cta.enterpriseId);
      const payload = result.sendPhoneVerificationCode;

      if (parseSendStatus(payload?.status) !== 'sent') {
        // Anything other than a confirmed `sent` opens nothing -- there is no in-flight
        // placeholder and nothing can resize.
        // `payload?.errorCode` is the API's wire value verbatim (rate_limited, invalid_number,
        // undeliverable, etc); a `quarantined`/`not_verifiable` status carries no errorCode, so
        // `mapSendFailure`'s own fallback kind is used in that case instead.
        const failure = mapSendFailure(payload);
        analytics.trackFailed(payload?.errorCode ?? undefined);
        setBannerError(failure);
        return;
      }

      analytics.trackCodeSent();
      setBannerError(null);

      const context: OtpHostContext = {
        phoneHint: payload?.phoneHint ?? null,
        dispensaryId: cta.dispensaryId,
        enterpriseId: cta.enterpriseId,
      };

      // The send has already completed by this point -- clear the loading state here, before
      // opening the modal, rather than waiting for `openModal` to resolve (i.e. for the modal to
      // CLOSE). Otherwise the button keeps spinning, hidden behind the modal overlay, for the
      // entire time the OTP dialog is open. `isSending` is only ever read to gate a NEW click
      // (above), and the modal overlay itself is what actually prevents interaction with the
      // banner while it's open, so clearing it here does not reopen the double-send window this
      // guard exists for. The `finally` below still clears it on every other (failure) path.
      setIsSending(false);

      const outcome = (await openModal<OtpHostContext, OtpHostOutcome>(ModalNames.phoneVerificationOtp, context)) as
        | OtpHostOutcome
        | undefined;

      if (outcome?.kind === 'verified') {
        // Owned here, not by the OTP host, even though the host is what actually resolved the
        // `verified` outcome -- the host only emits its own in-modal stages (otp_submitted/
        // otp_resent) to avoid this exact terminal event being double-counted. See the host's
        // doc comment.
        analytics.trackCompleted();
        setCta(null);
        user.refetch();
        return;
      }

      if (outcome?.kind === 'conflict') {
        analytics.trackFailed('conflict');
        // No bespoke handling: invalidate status and let the same effect that already re-derives
        // on a phone change recompute the banner. It will re-fetch, see `verified_other`, and
        // project the informational row from the `ownerEmailHint` that response carries.
        conflictRecoveryRef.current = true;
        setRefreshToken((token) => token + 1);
        return;
      }

      if (outcome?.kind === 'unavailable') {
        analytics.trackFailed();
        setBannerError({ kind: 'unavailable' });
        return;
      }

      // 'dismissed' or undefined: leave the CTA in its default state.
      analytics.trackDismissed();
    } catch {
      // A thrown error from `sendCode` itself (a genuine system fault) -- silent posture is not
      // appropriate here since the user just took a deliberate action; show the generic
      // unavailable state rather than a dead click.
      analytics.trackFailed();
      setBannerError({ kind: 'unavailable' });
    } finally {
      setIsSending(false);
    }
  }, [analytics, cta, isSending, sendCode, openModal, user]);

  return useMemo(
    () => ({ isVisible: cta !== null || bannerError !== null, isSending, cta, bannerError, onVerifyClick }),
    [cta, bannerError, isSending, onVerifyClick]
  );
}
